import type { Asset } from '@/shared/api/protocol-ts/model/dto_asset_pb';
import type { BBox } from '@/shared/api/protocol-ts/model/dto_common_geometry_pb';

import type { ToothSliceContext } from '@/entities/composed';

import {
  ToothSliceOriginReason,
  ToothSliceSource,
  ToothSliceSourceState,
  ToothSliceBox3,
  ToothSliceNumeration,
  ToothSliceDescriptor,
  ToothSliceRoi,
  ToothSliceVec3,
  VALID_FDI_ISO_NUMBERS,
  ValidFdiIso,
} from '../config';

import { isToothRoiAsset } from './isToothRoiAsset';
import { resolveToothGeometry } from './resolveToothGeometry';

const toBox3 = (box: BBox | undefined): ToothSliceBox3 | null => {
  if (!box?.X || !box.Y || !box.Z) {
    return null;
  }

  const values = [
    box.X.Min,
    box.X.Max,
    box.Y.Min,
    box.Y.Max,
    box.Z.Min,
    box.Z.Max,
  ];

  if (
    !values.every((value) => Number.isFinite(value)) ||
    box.X.Min >= box.X.Max ||
    box.Y.Min >= box.Y.Max ||
    box.Z.Min >= box.Z.Max
  ) {
    return null;
  }

  return {
    max: { x: box.X.Max, y: box.Y.Max, z: box.Z.Max },
    min: { x: box.X.Min, y: box.Y.Min, z: box.Z.Min },
  };
};

const toRoi = (roiAsset: Asset | undefined): ToothSliceRoi | null => {
  if (
    !roiAsset ||
    roiAsset.Kind.case !== 'GeneratedReport' ||
    roiAsset.Kind.value.Content.case !== 'CBCTGPMPRSubVolume'
  ) {
    return null;
  }

  const roi = roiAsset.Kind.value.Content.value;
  const outerBox = toBox3(roi.Position);

  if (!outerBox) {
    return null;
  }

  return {
    assetID: roiAsset.ID,
    innerBox: toBox3(roi.PositionInner),
    outerBox,
    windowLevel: roi.WindowLevel,
    windowWidth: roi.WindowWidth,
  };
};

const toSliceDescriptors = (assets: Asset[]): ToothSliceDescriptor[] =>
  assets.reduce<ToothSliceDescriptor[]>((descriptors, asset) => {
    if (
      asset.Kind.case !== 'GeneratedReport' ||
      asset.Kind.value.Content.case !== 'CBCTGPToothSlice'
    ) {
      return descriptors;
    }

    const slice = asset.Kind.value.Content.value;

    if (slice.Metadata.case !== 'Regular') {
      return descriptors;
    }

    const imageMetadata =
      slice.Slice?.Metadata.case === 'Image'
        ? slice.Slice.Metadata.value
        : null;
    const regular = slice.Metadata.value;

    return descriptors.concat({
      assetID: asset.ID,
      fileID: slice.Slice?.ID || null,
      imageHeight: imageMetadata?.Height ?? null,
      imageWidth: imageMetadata?.Width ?? null,
      order: regular.Order,
      orientation: slice.Orientation,
      positionMm: regular.Position,
      proposedToothCardOrder: slice.ProposedToothCardOrder,
      scale: slice.Scale,
      strideMm: regular.Stride,
      thicknessMm: regular.Thickness,
      totalImages: regular.TotalImages,
      windowLevel: slice.WindowLevel,
      windowWidth: slice.WindowWidth,
    });
  }, []);

export const isValidFdiIso = (iso: number): iso is ValidFdiIso =>
  VALID_FDI_ISO_NUMBERS.some((validIso) => validIso === iso);

const areSnapshotsReady = (context: ToothSliceContext) => {
  const { patientAssets, reportAssets, reportConditions } = context.snapshots;

  return (
    patientAssets.patientID === context.report.PatientID &&
    patientAssets.loading === 'succeeded' &&
    reportAssets.reportID === context.report.ID &&
    reportAssets.loading === 'succeeded' &&
    reportConditions.reportID === context.report.ID &&
    reportConditions.loading === 'succeeded'
  );
};

const makeLegacyInput = ({
  context,
  numeration,
  origin,
  reason,
  sliceDescriptors,
  volumeFileID,
}: {
  context: ToothSliceContext;
  numeration: ToothSliceNumeration | null;
  origin: ToothSliceVec3;
  reason: ToothSliceOriginReason;
  sliceDescriptors: ToothSliceDescriptor[];
  volumeFileID: string;
}): ToothSliceSource => ({
  isMissingTooth: Boolean(context.missingCondition),
  kind: 'legacy',
  numeration,
  origin,
  reason,
  reportID: context.report.ID,
  sliceDescriptors,
  toothID: context.tooth.ID,
  volumeFileID,
});

export const makeToothSliceSource = (
  context: ToothSliceContext | null,
): ToothSliceSource | null => {
  if (!context || !areSnapshotsReady(context) || !context.volumeFileID) {
    return null;
  }

  const { volumeFileID } = context;

  const protocolNumeration = context.tooth.Numeration;
  const numeration = protocolNumeration
    ? {
        iso: protocolNumeration.ISO,
        supernumeraryIndex: protocolNumeration.SupernumeraryIndex,
      }
    : null;
  const sliceDescriptors = toSliceDescriptors(context.toothAssets);

  const roiAssets = context.toothAssets.filter(isToothRoiAsset);
  const roi = roiAssets.length === 1 ? toRoi(roiAssets[0]) : null;
  const geometry = resolveToothGeometry({
    tooth: context.tooth,
  });

  if (!geometry) {
    return null;
  }

  const { origin } = geometry;

  if (context.missingCondition) {
    return makeLegacyInput({
      context,
      numeration,
      origin,
      reason: 'missing-tooth-policy',
      sliceDescriptors,
      volumeFileID,
    });
  }

  if (!numeration) {
    return makeLegacyInput({
      context,
      numeration,
      origin,
      reason: 'missing-numeration',
      sliceDescriptors,
      volumeFileID,
    });
  }

  if (!isValidFdiIso(numeration.iso)) {
    return makeLegacyInput({
      context,
      numeration,
      origin,
      reason: 'invalid-iso',
      sliceDescriptors,
      volumeFileID,
    });
  }

  const { anatomicalBasis } = geometry;

  if (!anatomicalBasis) {
    return makeLegacyInput({
      context,
      numeration,
      origin,
      reason: 'missing-anatomical-basis',
      sliceDescriptors,
      volumeFileID,
    });
  }

  if (roiAssets.length > 1) {
    return makeLegacyInput({
      context,
      numeration,
      origin,
      reason: 'ambiguous-roi',
      sliceDescriptors,
      volumeFileID,
    });
  }

  if (!roi) {
    return makeLegacyInput({
      context,
      numeration,
      origin,
      reason: 'missing-roi',
      sliceDescriptors,
      volumeFileID,
    });
  }

  return {
    anatomicalBasis,
    geometrySource: geometry.source,
    isMissingTooth: Boolean(context.missingCondition),
    kind: 'tooth',
    numeration: {
      ...numeration,
      iso: numeration.iso,
    },
    origin,
    reportID: context.report.ID,
    roi,
    sliceDescriptors,
    toothID: context.tooth.ID,
    volumeFileID,
  };
};

export const resolveToothSliceSourceState = (
  context: ToothSliceContext | null,
): ToothSliceSourceState => {
  if (!context) {
    return { status: 'pending', source: null };
  }

  const { patientAssets, reportAssets, reportConditions } = context.snapshots;
  const isCurrentSnapshot =
    patientAssets.patientID === context.report.PatientID &&
    reportAssets.reportID === context.report.ID &&
    reportConditions.reportID === context.report.ID;

  if (!isCurrentSnapshot) {
    return { status: 'pending', source: null };
  }

  if (
    [patientAssets, reportAssets, reportConditions].some(
      (snapshot) => snapshot.loading === 'failed',
    )
  ) {
    return { status: 'failed', source: null };
  }

  if (!areSnapshotsReady(context)) {
    return { status: 'pending', source: null };
  }

  return { status: 'succeeded', source: makeToothSliceSource(context) };
};
